import { Box, Button, Flex, IconButton, Spinner, Text } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import moment from 'moment';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { DeleteIcon, EditIcon, ResyncBlueIcon } from '@assets/icons/index';
import { ConfirmationState2 } from '@assets/images';
import { formatCurrency } from '@helpers/utils';
import BundleModal from '@pages/UploadSellout/components/convert/product-list/bundleModal';
import { useDataKonversi } from '@pages/UploadSellout/hooks/state/useDataKonversi';
import { useSkuUpsertModal } from '@pages/UploadSellout/hooks/state/useSkuUpsertModal';
import Axios from '@services/authAxios';
import { useUserInfo } from '@store/userInfo';
import { useSnackbarNotification } from '@store/snackbarNotification';
import { useConfirmModal } from '@store/useConfirmModal';
import styles from './styles.module.css';

const ActionButton = ({ values }) => {
    const queryClient = useQueryClient();
    const { userInfo } = useUserInfo();
    const { deleteData, editData } = useDataKonversi();
    const { set: setConfirmModal } = useConfirmModal();
    const { openEdit } = useSkuUpsertModal();
    const { onChangeSnackbarNotification } = useSnackbarNotification();

    const [isLoading, setIsLoading] = useState(false);

    const buttonStyle = {
        paddingX: 0,
        size: 'sm',
        variant: 'outline',
        borderRadius: '4px',
        color: 'neutral.200',
        borderColor: 'neutral.200',
    };

    const resync = async () => {
        setIsLoading(true);
        const searchValue = values.barcode || values.code;
        try {
            const { data } = await queryClient.fetchQuery({
                queryKey: ['master-product', searchValue],
                staleTime: 0,
                queryFn: async () => {
                    const { data } = await Axios.get('/api/v1/master-data/product', {
                        params: { limit: 20, search: searchValue, customer_id: userInfo?.customer_id },
                    });

                    return data;
                },
            });
            if (data.page_data?.length > 0) {
                editData(values.id, {
                    name: data?.page_data[0].name,
                });
                onChangeSnackbarNotification({
                    status: 'success',
                    title: 'Berhasil Menambahkan SKU',
                    description: (
                        <>
                            SKU <b>{data.page_data[0].name}</b> telah ditambahkan
                        </>
                    ),
                    duration: 3,
                });
            } else {
                onChangeSnackbarNotification({
                    status: 'error',
                    title: 'Gagal Menambahkan SKU',
                    description: 'Produk tidak ditemukan',
                    duration: 3,
                });
            }
        } catch (error) {
            const errorMessage = error?.response?.data?.message ?? 'Terjadi kesalahan saat menambahkan SKU';
            onChangeSnackbarNotification({
                status: 'error',
                title: 'Gagal Menambahkan SKU',
                description: errorMessage,
                duration: 3,
            });
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Flex justifyContent="center" gap="8px" width="100%" className={styles['column__container']}>
            <IconButton
                icon={isLoading ? <Spinner /> : <ResyncBlueIcon />}
                aria-label="sync product"
                isDisabled={isLoading || (!values.barcode && !values.code)}
                {...buttonStyle}
                onClick={resync}
            />
            <IconButton
                icon={<EditIcon />}
                aria-label="edit product"
                {...buttonStyle}
                onClick={() => {
                    const toEditData = {
                        ...values,
                        customer_subtotal: values?.customer_subtotal ?? '',
                        customer_final_subtotal: values?.customer_final_subtotal ?? '',
                    };
                    openEdit(toEditData);
                }}
            />
            <IconButton
                icon={<DeleteIcon />}
                aria-label="delete product"
                {...buttonStyle}
                onClick={() => {
                    setConfirmModal({
                        isOpen: true,
                        ConfirmationStateImage: ConfirmationState2,
                        title: 'Hapus SKU?',
                        desc: 'Data yang telah dihapus tidak dapat dikembalikan',
                        btnLabelYes: 'Ya, hapus',
                        btnLabelNo: 'Kembali',
                        onSubmit: () => deleteData(values.id),
                    });
                }}
            />
        </Flex>
    );
};

const ProductColumn = ({ value, values }) => {
    const [open, setOpen] = useState(false);

    const hasBarcode = !!values?.barcode || !!values?.code;
    const hasProduct = value && hasBarcode;
    const isBundle = Array.isArray(values?.product_list) && values.product_list.length > 0;

    if (hasProduct && isBundle)
        return (
            <>
                <BundleModal isOpen={open} onClose={() => setOpen(false)} bundleData={values} />
                <Box>
                    <Text>Bundle Product {value}</Text>
                    <Button variant="unstyled" height="fit-content" paddingX={0} onClick={() => setOpen(true)}>
                        <Text decoration="underline" color="sagitarius.500">
                            Daftar Produk
                        </Text>
                    </Button>
                </Box>
            </>
        );

    if (hasProduct)
        return (
            <Box>
                <Text>{value}</Text>
                {values?.code && <Text color="neutral.500">{values.code}</Text>}
            </Box>
        );

    if (hasBarcode)
        return (
            <Text color="danger.500" style={{ lineClamp: 3, WebkitLineClamp: 3 }}>
                Tidak ada produk yang sesuai atau produk belum melalui proses mapping.{' '}
                <Link to="/upload-mapping-v2" target="_blank">
                    <Text as="span" textDecoration="underline" color="inherit">
                        Lihat Product Mapping
                    </Text>
                </Link>
            </Text>
        );

    return <Text>-</Text>;
};

export const tableList = [
    {
        name: 'Sell Out Date',
        value: 'date',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value }) => {
            const selectedTimeUploadType = JSON.parse(localStorage.getItem('selectedTimeUploadType'));
            const date = moment(value).isValid() ? (
                moment(value).format(selectedTimeUploadType === 'daily' ? 'DD MMM YYYY' : 'MMM YYYY')
            ) : selectedTimeUploadType === 'monthly' ? (
                moment().subtract(1, 'month').format('MMM YYYY')
            ) : (
                <Box color={'danger.500'}>Tanggal belum diinput</Box>
            );

            return (
                <Flex className={styles['column__container']}>
                    <Text>{date}</Text>
                </Flex>
            );
        },
    },
    {
        name: 'Cust. SKU/Barcode',
        value: 'barcode',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value, values }) => {
            const displayValue = value || values?.code;
            const isEmptyValue = !displayValue;
            return (
                <Flex className={styles['column__container']}>
                    <Text color={isEmptyValue ? 'danger.500' : 'inherit'}>
                        {isEmptyValue ? 'Barcode tidak terbaca' : displayValue}
                    </Text>
                </Flex>
            );
        },
    },
    {
        name: 'Product Description',
        value: 'name',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value, values }) => (
            <Flex className={styles['column__container']}>
                <ProductColumn value={value} values={values} />
            </Flex>
        ),
    },
    {
        name: 'Quantity',
        value: 'qty',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value }) => (
            <Flex className={styles['column__container']}>
                <Text>{value == null ? '-' : Number(value).toLocaleString()}</Text>
            </Flex>
        ),
    },
    {
        name: 'Subtotal',
        value: 'customer_subtotal',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value }) => (
            <Flex className={styles['column__container']}>
                <Text>{value ? formatCurrency(value) : '-'}</Text>
            </Flex>
        ),
    },
    {
        name: 'Final Subtotal',
        value: 'customer_final_subtotal',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value }) => (
            <Flex className={styles['column__container']}>
                <Text>{value ? formatCurrency(value) : '-'}</Text>
            </Flex>
        ),
    },
    {
        name: 'Parahub ID',
        value: 'customer_parahub_id',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value }) => (
            <Flex className={styles['column__container']}>
                <Text>{value ?? '-'}</Text>
            </Flex>
        ),
    },
    {
        name: 'Parahub',
        value: 'customer_parahub_name',
        justifyContent: 'flex-start',
        withSort: true,
        component: ({ value }) => (
            <Flex className={styles['column__container']}>
                <Text>{value ?? '-'}</Text>
            </Flex>
        ),
    },
    {
        name: 'Action',
        width: 120,
        justifyContent: 'center',
        component: ({ values }) => <ActionButton values={values} />,
    },
];
